// Per-vertical config for the LeadFlow landing pages.
// Every slug here MUST match _LANDING_SLUG_TO_ACCIDENT_TYPE in backend/routes/public_routes.py.

// Shared testimonials + case results used across every vertical.
// Photos are neutral Unsplash portraits; quotes are generic "how the process
// worked" — deliberately NOT outcome projections (per TCPA / attorney-ad rules).
export const TESTIMONIALS = [
  {
    name: "Marcus J.",
    caseTag: "Auto Accident · Georgia",
    quote:
      "After my accident I had no idea what to do. Within 20 minutes of submitting the form an attorney called me. They handled everything with the insurance company so I could focus on healing.",
    photo:
      "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?auto=format&fit=facearea&facepad=2&w=200&h=200&q=80",
    stars: 5,
  },
  {
    name: "Sarah T.",
    caseTag: "Slip & Fall · Texas",
    quote:
      "The whole process was so much easier than I expected. My attorney explained every step and I never had to chase anyone for an update. Couldn't be happier with how they treated me.",
    photo:
      "https://images.unsplash.com/photo-1573497019940-1c28c88b4f3e?auto=format&fit=facearea&facepad=2&w=200&h=200&q=80",
    stars: 5,
  },
  {
    name: "James R.",
    caseTag: "Motorcycle Crash · Florida",
    quote:
      "I was worried about attorney fees and out-of-pocket costs. They explained the no-win-no-fee arrangement upfront and stood by it. Real professionals from start to finish.",
    photo:
      "https://images.unsplash.com/photo-1560250097-0b93528c311a?auto=format&fit=facearea&facepad=2&w=200&h=200&q=80",
    stars: 5,
  },
];

// "Recent Case Results" carousel — deliberately anonymised, with the required
// legal disclaimer at the bottom of the section: "Past results do not
// guarantee future outcomes." No outcome projections shown for the visitor's
// own case (that would be a TCPA / attorney-advertising red flag).
export const CASE_RESULTS = [
  { amount: "$4.2M", label: "Rear-end collision · brain injury settlement" },
  { amount: "$2.8M", label: "Trucking company negligence · fatal MVA" },
  { amount: "$1.6M", label: "Slip & fall · commercial property" },
  { amount: "$950K", label: "Rideshare passenger · multi-vehicle crash" },
  { amount: "$780K", label: "Motorcycle · uninsured motorist" },
  { amount: "$540K", label: "Nursing home neglect · pressure injuries" },
];

// Trust badges — all statements are content-verifiable (not accolade claims).
export const TRUST_BADGES = [
  { icon: "shield", title: "24/7 case review", sub: "Attorneys standing by any time" },
  { icon: "check", title: "No win, no fee", sub: "Free unless we recover for you" },
  { icon: "lock", title: "Confidential", sub: "Your info is protected & private" },
  { icon: "scale", title: "Licensed attorneys", sub: "State-licensed injury lawyers" },
];

// All-in-one PI landing page case-type dropdown. `key` MUST match the
// backend's _CASE_TYPE_TO_ACCIDENT_TYPE mapping.
export const PI_CASE_TYPES = [
  { key: "auto",               label: "Auto / Car Accident" },
  { key: "truck",              label: "Truck or 18-Wheeler Accident" },
  { key: "motorcycle",         label: "Motorcycle Accident" },
  { key: "rideshare",          label: "Uber / Lyft (Rideshare)" },
  { key: "slip_fall",          label: "Slip and Fall / Premises Liability" },
  { key: "medical_malpractice",label: "Medical Malpractice" },
  { key: "product_liability",  label: "Defective Product / Product Liability" },
  { key: "nursing_home",       label: "Nursing Home Abuse or Neglect" },
  { key: "wrongful_death",     label: "Wrongful Death" },
  { key: "dog_bite",           label: "Dog Bite / Animal Attack" },
  { key: "workers_comp",       label: "Workers' Compensation" },
  { key: "torts",              label: "Other Tort / Personal Injury" },
  { key: "other",              label: "Something else — not sure yet" },
];

// Per-vertical page copy. Every slug here MUST also be registered in App.js
// and in the backend's _LANDING_SLUG_TO_ACCIDENT_TYPE dict.
export const VERTICALS = {
  "auto-accident": {
    slug: "auto-accident",
    accidentType: "auto",
    kicker: "Auto Accident Attorneys",
    headline: "Injured in a Car Accident?",
    headlineAccent: "You may be owed compensation.",
    subheadline:
      "Medical bills, lost wages, and pain & suffering add up fast. Speak with an experienced injury attorney about your case — free, no obligation, no fee unless we win.",
    heroImage:
      "https://images.unsplash.com/photo-1590247813693-5541d1c609fd?auto=format&fit=crop&w=1600&q=75",
    urgencyBadge: "Free 24/7 Case Review",
    stat1: { number: "24hr", label: "Attorney response guaranteed" },
    stat2: { number: "No fee", label: "Unless we recover for you" },
    stat3: { number: "100%", label: "Confidential" },
    qualifyingQuestion: "When was the accident?",
    incidentLabel: "Accident Date",
    caseDescriptionLabel: "Briefly describe what happened",
    ctaText: "Get My Free Case Review",
    caseTypeSelector: false,
  },
  "truck-accident": {
    slug: "truck-accident",
    accidentType: "truck",
    kicker: "Truck & 18-Wheeler Accident Attorneys",
    headline: "Hit by a Semi Truck or Commercial Vehicle?",
    headlineAccent: "Serious injuries deserve serious representation.",
    subheadline:
      "Trucking companies keep teams of lawyers on retainer. You deserve one on your side. Cases involving 18-wheelers, delivery trucks, and commercial fleets often involve complex insurance layers.",
    heroImage:
      "https://images.unsplash.com/photo-1601584115197-04ecc0da31d7?auto=format&fit=crop&w=1600&q=75",
    urgencyBadge: "Serious Injury? Get Help Now",
    stat1: { number: "24hr", label: "Attorney response guaranteed" },
    stat2: { number: "No fee", label: "Unless we recover for you" },
    stat3: { number: "100%", label: "Confidential" },
    qualifyingQuestion: "When did the truck accident happen?",
    incidentLabel: "Accident Date",
    caseDescriptionLabel: "Describe the truck accident (semi, delivery, bus, etc.)",
    ctaText: "Get My Free Truck Case Review",
    caseTypeSelector: false,
  },
  "rideshare-accident": {
    slug: "rideshare-accident",
    accidentType: "rideshare",
    kicker: "Uber & Lyft Accident Attorneys",
    headline: "Hurt in an Uber or Lyft?",
    headlineAccent: "Rideshare cases are different — get the right attorney.",
    subheadline:
      "Rideshare accidents have unique insurance rules — most riders don't know Uber & Lyft carry $1M policies. Don't accept a lowball offer from an insurance adjuster without talking to an attorney.",
    heroImage:
      "https://images.unsplash.com/photo-1449965408869-eaa3f722e40d?auto=format&fit=crop&w=1600&q=75",
    urgencyBadge: "$1M Uber/Lyft Coverage",
    stat1: { number: "24hr", label: "Attorney response guaranteed" },
    stat2: { number: "No fee", label: "Unless we recover for you" },
    stat3: { number: "100%", label: "Confidential" },
    qualifyingQuestion: "When did the rideshare accident happen?",
    incidentLabel: "Accident Date",
    caseDescriptionLabel: "Were you a passenger, driver, or in another vehicle?",
    ctaText: "Get My Free Rideshare Review",
    caseTypeSelector: false,
  },
  "motorcycle-accident": {
    slug: "motorcycle-accident",
    accidentType: "motorcycle",
    kicker: "Motorcycle Accident Attorneys",
    headline: "Down on Your Bike Because of Someone Else?",
    headlineAccent: "Riders deserve a fair shake. Fight the bias.",
    subheadline:
      "Motorcyclists face insurance bias — you need an attorney who knows how to counter that and fight for every dollar you're entitled to for medical bills, lost wages, and pain and suffering.",
    heroImage:
      "https://images.unsplash.com/photo-1558981285-6f0c94958bb6?auto=format&fit=crop&w=1600&q=75",
    urgencyBadge: "Riders Get Less Without a Lawyer",
    stat1: { number: "24hr", label: "Attorney response guaranteed" },
    stat2: { number: "No fee", label: "Unless we recover for you" },
    stat3: { number: "100%", label: "Confidential" },
    qualifyingQuestion: "When did the crash happen?",
    incidentLabel: "Crash Date",
    caseDescriptionLabel: "Describe the crash",
    ctaText: "Get My Free Rider Case Review",
    caseTypeSelector: false,
  },
  // ---------- All-in-one PI landers ------------------------------------
  "personal-injury": {
    slug: "personal-injury",
    accidentType: "other",
    kicker: "Personal Injury Attorneys",
    headline: "Hurt Because of Someone Else?",
    headlineAccent: "Your free case review starts here.",
    subheadline:
      "Motor-vehicle crashes, slip and falls, medical malpractice, defective products, dog bites, nursing-home neglect, wrongful death — if someone else is responsible for your injury, you deserve to speak with an experienced attorney about what you might be entitled to.",
    heroImage:
      "https://images.unsplash.com/photo-1589829545856-d10d557cf95f?auto=format&fit=crop&w=1600&q=75",
    urgencyBadge: "Free 24/7 Case Review",
    stat1: { number: "24hr", label: "Attorney response guaranteed" },
    stat2: { number: "No fee", label: "Unless we recover for you" },
    stat3: { number: "100%", label: "Confidential" },
    qualifyingQuestion: "When did the incident happen?",
    incidentLabel: "Date of Incident",
    caseDescriptionLabel: "Briefly describe what happened",
    ctaText: "Get My Free Case Review",
    caseTypeSelector: true,
  },
  "free-case-review": {
    slug: "free-case-review",
    accidentType: "other",
    kicker: "Free Case Review — Personal Injury",
    headline: "Injured? Talk to a Lawyer for Free.",
    headlineAccent: "No obligation. No fee unless we win.",
    subheadline:
      "Answer a few quick questions and get a free, confidential case review from an experienced attorney within 24 hours. We handle every type of personal injury case — auto, truck, motorcycle, rideshare, slip and fall, medical malpractice, wrongful death, and more.",
    heroImage:
      "https://images.unsplash.com/photo-1521791136064-7986c2920216?auto=format&fit=crop&w=1600&q=75",
    urgencyBadge: "Free 24/7 Attorney Review",
    stat1: { number: "24hr", label: "Attorney response guaranteed" },
    stat2: { number: "No fee", label: "Unless we recover for you" },
    stat3: { number: "100%", label: "Confidential" },
    qualifyingQuestion: "When did the incident happen?",
    incidentLabel: "Date of Incident",
    caseDescriptionLabel: "Tell us in a few sentences what happened",
    ctaText: "Get My Free Case Review",
    caseTypeSelector: true,
  },
};

// The full TCPA consent language — server records the verbatim string.
// Iter-45: names Conecme (Jellis Enterprise LLC) as the marketing platform.
// Custom buyer landing pages typically override this with their own admin-
// authored language that names the specific firm.
export const TCPA_LANGUAGE =
  "By clicking below, I provide my ESIGN and prior express written consent " +
  "to be contacted by Conecme (a Jellis Enterprise LLC company) and one " +
  "or more of its attorney or law firm marketing partners at the phone " +
  "number and email address I provided (including any wireless number) " +
  "using automated telephone dialing systems, artificial or prerecorded " +
  "voices, text (SMS/MMS) messages, and email — even if my number is on " +
  "a Do Not Call or internal list. Message and data rates may apply. " +
  "Consent is NOT a condition of purchase or receiving legal services. " +
  "I may revoke consent at any time by texting STOP. I confirm I am 18+ " +
  "and have read the Privacy Policy and Terms of Service.";

// Iter-45: platform brand + parent for dual-branding disclosure.
// `let` so the landing domain (e.g. casequoter.com) can show its own brand — see applySiteBrand.
export let PLATFORM_BRAND = "Conecme";
export const PARENT_COMPANY = "Jellis Enterprise LLC";
export const PLATFORM_TAGLINE = "A Jellis Enterprise LLC company";
export const PLATFORM_PHONE = "1-800-555-0199";

let siteBrand = null;
// On the landing domain: its brand replaces Conecme everywhere a visitor reads it (header, footer, consent).
export function applySiteBrand(brand) {
  if (!brand || brand === siteBrand) return;
  siteBrand = brand;
  PLATFORM_BRAND = brand;
}
// Consent / platform wording as shown on this site (the server records the same swap). Only the
// platform phrase changes, so firm names that contain "Conecme" stay as they are.
const PLATFORM_PHRASE = "Conecme (a Jellis Enterprise LLC company)";
export const siteText = (t) => (siteBrand && t ? t.split(PLATFORM_PHRASE).join(`${siteBrand} (a Jellis Enterprise LLC company)`) : t);
